import formatName from 'I18n/utils/formatName';
import { createContext, useContext, useReducer } from 'react';
import { setIn } from '../utils/objectHelpers';
import { hsToStripePaymentMethod, isBnpl } from 'payments-checkout-lib/utils/paymentMethods';
import { jsx as _jsx } from "react/jsx-runtime";
export function init({
  session,
  formStateOverride
}) {
  const {
    prefillData
  } = session;
  const customerEmail = session.customerEmail || (prefillData === null || prefillData === void 0 ? void 0 : prefillData.email) || '';
  const isEmailLocked = session.shouldLockEmail === true && Boolean(session.customerEmail);
  const shippableCountries = session.shippableCountries;
  const onlyShippingCountryOrEmptyString = shippableCountries && shippableCountries.length === 1 ? shippableCountries[0].toLowerCase() : '';
  const paymentMethods = new Set(session.paymentMethods.map(paymentMethodConfig => paymentMethodConfig.paymentMethod));
  const hasCardPaymentMethod = paymentMethods.has('CARD');
  const customerData = {
    firstName: (prefillData === null || prefillData === void 0 ? void 0 : prefillData.firstName) || '',
    lastName: (prefillData === null || prefillData === void 0 ? void 0 : prefillData.lastName) || ''
  };
  const fullName = customerData.firstName && customerData.lastName ? formatName({
    firstName: customerData.firstName,
    lastName: customerData.lastName
  }) : '';
  const paymentMethod = hasCardPaymentMethod ? 'CARD' : [...paymentMethods][0];
  const initialFormState = Object.assign({
    bankInfo: {
      accountHolderName: '',
      accountHolderType: 'INDIVIDUAL',
      transactionType: 'BUSINESS'
    },
    billingAddress: {
      fullName,
      address: '',
      address2: '',
      city: '',
      country: onlyShippingCountryOrEmptyString,
      postalCode: '',
      state: ''
    },
    billingSameAsShipping: !!shippableCountries.length,
    collectPaymentMethodOnFileAcknowledged: false,
    clickToCancelAcknowledged: false,
    customData: {},
    discountCode: '',
    discountCodeError: false,
    email: customerEmail,
    isEmailLocked,
    isCheckoutSubmitPending: false,
    isDiscountCodeApplied: false,
    loading: false,
    merchantTermsOfServiceAcknowledged: false,
    pageError: false,
    paymentMethod,
    stripePaymentMethod: hsToStripePaymentMethod(paymentMethod),
    phoneNumbers: {},
    shippingAddress: Object.assign({}, customerData, {
      address: '',
      address2: '',
      city: '',
      country: onlyShippingCountryOrEmptyString,
      postalCode: '',
      state: ''
    }),
    stripeError: null,
    stripeValidationError: false,
    paymentElementLoadError: false
  }, formStateOverride);
  return initialFormState;
}
export function reducer(state, action) {
  switch (action.type) {
    case 'setPaymentMethod':
      {
        const nextPaymentMethod = action.data.value;
        return Object.assign({}, state, {
          paymentMethod: nextPaymentMethod
        }, isBnpl(nextPaymentMethod) && {
          collectPaymentMethodOnFileAcknowledged: false
        });
      }
    case 'setStripePaymentMethod':
      return Object.assign({}, state, {
        stripePaymentMethod: action.data.value
      });
    case 'setShippingAddressField':
      {
        const {
          name,
          value
        } = action.data;
        let updatedState = state;
        const newCountrySelected = name === 'country' && state.shippingAddress.country !== value;
        if (newCountrySelected) {
          updatedState = setIn(updatedState, ['shippingAddress', 'state'], '');
        }
        if (state.billingSameAsShipping) {
          if (newCountrySelected) {
            updatedState = setIn(updatedState, ['billingAddress', 'state'], '');
          }
          if (name === 'firstName' || name === 'lastName') {
            updatedState = setIn(updatedState, ['billingAddress', 'fullName'], formatName({
              firstName: name === 'firstName' ? value : state.shippingAddress.firstName,
              lastName: name === 'lastName' ? value : state.shippingAddress.lastName
            }));
          } else {
            updatedState = setIn(updatedState, ['billingAddress', name], value);
          }
        }
        return setIn(updatedState, ['shippingAddress', name], value);
      }
    case 'setBillingAddressField':
      return setIn(state, ['billingAddress', action.data.name], action.data.value);
    case 'setBillingSameAsShipping':
      {
        if (!action.data.value) {
          return Object.assign({}, state, {
            billingSameAsShipping: false
          });
        }
        const {
          shippingAddress
        } = state;
        return Object.assign({}, state, {
          billingSameAsShipping: true,
          billingAddress: {
            fullName: formatName({
              firstName: shippingAddress.firstName,
              lastName: shippingAddress.lastName
            }),
            address: shippingAddress.address,
            address2: shippingAddress.address2,
            city: shippingAddress.city,
            state: shippingAddress.state,
            postalCode: shippingAddress.postalCode,
            country: shippingAddress.country
          }
        });
      }
    case 'setBankInfoField':
      return setIn(state, ['bankInfo', action.data.name], action.data.value);
    case 'setField':
      return setIn(state, Array.isArray(action.data.propertyPath) ? action.data.propertyPath : [action.data.propertyPath], action.data.value);
    case 'setStripeError':
      return Object.assign({}, state, {
        stripeError: action.data
      });
    case 'resetStripeError':
      return Object.assign({}, state, {
        stripeError: null
      });
    case 'setStripeValidationError':
      return Object.assign({}, state, {
        stripeValidationError: true
      });
    case 'resetStripeValidationError':
      return Object.assign({}, state, {
        stripeValidationError: false
      });
    case 'setPaymentElementLoadError':
      return Object.assign({}, state, {
        paymentElementLoadError: true
      });
    case 'setDiscountCode':
      return Object.assign({}, state, {
        discountCode: action.data
      });
    case 'setDiscountCodeError':
      return Object.assign({}, state, {
        discountCodeError: action.data
      });
    case 'setMerchantTermsOfServiceAcknowledged':
      return Object.assign({}, state, {
        merchantTermsOfServiceAcknowledged: action.data
      });
    case 'setCollectPaymentMethodOnFileAcknowledged':
      return Object.assign({}, state, {
        collectPaymentMethodOnFileAcknowledged: action.data
      });
    case 'setClickToCancelAcknowledged':
      return Object.assign({}, state, {
        clickToCancelAcknowledged: action.data
      });
    case 'setIsDiscountCodeApplied':
      return Object.assign({}, state, {
        isDiscountCodeApplied: action.data
      });
    case 'setIsCheckoutSubmitPending':
      return Object.assign({}, state, {
        isCheckoutSubmitPending: action.data.value
      });
    case 'setPhoneNumbers':
      return Object.assign({}, state, {
        phoneNumbers: action.data
      });
    case 'setPageError':
      return Object.assign({}, state, {
        pageError: action.data
      });
    default:
      return state;
  }
}
export const PaymentFormContext = /*#__PURE__*/createContext(undefined);

/**
 * Context for managing the state of the form
 *
 * @param {node || element || node[] || element[]} children     children that will be rendered to display the form
 * @param {CheckoutSessionGetResponse} checkoutSession         the checkout session that provides the data to initialize the form
 * @returns {node || element || node[] || element[]}            children rendered inside of the context provider
 */
export const PaymentFormContextProvider = ({
  children,
  checkoutSession,
  formStateOverride
}) => {
  const initialArg = {
    session: checkoutSession,
    formStateOverride
  };
  const [state, dispatch] = useReducer(reducer, initialArg, init);
  const contextValue = {
    formState: state,
    dispatch,
    checkoutSession
  };
  return /*#__PURE__*/_jsx(PaymentFormContext.Provider, {
    value: contextValue,
    children: children
  });
};

/**
 * a hook to access PaymentFormContext
 *
 * @returns {Object<{ formState, checkoutSession, dispatch }>}    object that contains formState, dispatch, and checkoutSession
 * formState                                                      object that contains the state for the checkout form
 * checkoutSession                                                the input to `PaymentFormContextProvider`
 * dispatch                                                       dispatcher function to update formState
 */
export const usePaymentFormContext = () => {
  const context = useContext(PaymentFormContext);
  if (context === undefined) {
    throw new Error('Please provide PaymentFormContext in order to use this hook');
  }
  return context;
};